import type { AllHTMLAttributes, ReactNode } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { getClickableElementStyles } from '../../styles/clickable-element/clickable-element';
import { Box } from '../box/box';
import { type FieldContainerBaseProps } from '../field-container/field-container';
import { Text } from '../text/text';

import * as styles from './document-uploader-input.css';

export interface DocumentUploaderInputProps
  extends FieldContainerBaseProps,
    Partial<
      Pick<
        AllHTMLAttributes<HTMLInputElement>,
        'required' | 'accept' | 'onChange'
      >
    > {
  id: string;
  'data-testid'?: string;
  'aria-describedby'?: string;
  validating?: boolean;
  children: ReactNode;
}

export const DocumentUploaderInput = ({
  children,
  disabled = false,
  validating,
  onChange,
  required = false,
  accept = 'image/jpeg,image/png,application/pdf',
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  ...inputProps
}: DocumentUploaderInputProps) => {
  const {
    styles: clickableStyles,
    padding,
    textVariant,
  } = getClickableElementStyles({
    size: 'medium',
    variant: 'outline',
    disabled: disabled || validating,
    hasLeftIcon: false,
    hasRightIcon: false,
  });

  return (
    <Box
      Component="label"
      expand
      rounded
      padding={padding}
      constrain="hidden"
      alignContent={{ y: 'center' }}
      htmlFor={inputProps.id}
      className={classFilter([styles.inputLabelWrapper, clickableStyles])}
    >
      <input
        {...inputProps}
        className={styles.inputWithHiddenAppearance}
        onClick={(e) => {
          if (e.target instanceof HTMLInputElement && e.target.value) {
            // If the input already has a value, we clear it to allow re-uploading the same file
            e.target.value = '';
          }
        }}
        onChange={onChange}
        type="file"
        disabled={disabled}
        aria-required={required}
        accept={accept}
        aria-invalid={invalid}
        aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
      />

      <Text variant={textVariant} color="currentColor" align="center">
        {children}
      </Text>
    </Box>
  );
};
